import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    BarChart3,
    Calendar,
    Car,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    ChevronUp,
    FileClock,
    FileText,
    FileWarning,
    FolderOpen,
    Loader2,
    RefreshCw,
    Scale,
    TrendingUp,
    Users,
    X,
} from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import ChartBox from "./charts/ChartBox";
import { sigortaDosyaListeOzet } from "../lib/api";
import TahkimSureDetayPanel from "./TahkimSureDetayPanel";

const LS_EXPANDED = "sigortaDosyaTakipExpanded";

const AYLAR = [
    { value: 0, label: "Tüm yıl" },
    { value: 1, label: "Oca" },
    { value: 2, label: "Şub" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Nis" },
    { value: 5, label: "May" },
    { value: 6, label: "Haz" },
    { value: 7, label: "Tem" },
    { value: 8, label: "Ağu" },
    { value: 9, label: "Eyl" },
    { value: 10, label: "Eki" },
    { value: 11, label: "Kas" },
    { value: 12, label: "Ara" },
];

const YILLAR = [2023, 2024, 2025, 2026, 2027];

const UYARI_STIL = {
    warning: "border-amber-500/35 bg-amber-500/10 text-amber-100",
    danger: "border-rose-500/35 bg-rose-500/10 text-rose-100",
    info: "border-sky-500/35 bg-sky-500/10 text-sky-100",
};

const UYARI_IKON = {
    file: FileWarning,
    calendar: Calendar,
    alert: AlertTriangle,
    scale: Scale,
};

const KPI_THEME = {
    violet: { border: "border-violet-500/40", bg: "bg-violet-500/10", text: "text-violet-200", dot: "#8B5CF6" },
    sky: { border: "border-sky-500/40", bg: "bg-sky-500/10", text: "text-sky-200", dot: "#38BDF8" },
    gold: { border: "border-[#D4AF37]/45", bg: "bg-[#D4AF37]/10", text: "text-[#F3E5AB]", dot: "#D4AF37" },
    amber: { border: "border-amber-500/40", bg: "bg-amber-500/10", text: "text-amber-200", dot: "#F59E0B" },
    rose: { border: "border-rose-500/40", bg: "bg-rose-500/10", text: "text-rose-200", dot: "#F43F5E" },
    emerald: { border: "border-emerald-500/40", bg: "bg-emerald-500/10", text: "text-emerald-200", dot: "#10B981" },
};

function KpiChip({ label, value, accent = "violet", active, onClick, title }) {
    const t = KPI_THEME[accent] || KPI_THEME.violet;
    const Tag = onClick ? "button" : "div";
    return (
        <Tag
            type={onClick ? "button" : undefined}
            onClick={onClick}
            title={title || label}
            className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 min-w-0 transition ${t.border} ${t.bg} ${
                onClick ? "hover:brightness-110 cursor-pointer" : ""
            } ${active ? "ring-1 ring-white/30" : ""}`}
        >
            <span className="text-[10px] uppercase tracking-wide text-slate-500 truncate">{label}</span>
            <span className={`text-base font-semibold tabular-nums leading-none ${t.text}`}>{value ?? "—"}</span>
            {onClick && <ChevronRight className="h-3 w-3 text-slate-600 shrink-0 opacity-60" />}
        </Tag>
    );
}

function formatTrDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("tr-TR");
    } catch {
        return iso;
    }
}

function UyariDosyaListesi({ dosyalar, variant }) {
    if (!dosyalar?.length) return null;
    const isTahkim =
        variant === "tahkim" ||
        variant === "on_tahkim_aktif" ||
        dosyalar.some((d) => d.on_tahkim_sure_durumu || d.dosya_durumu === "ON_TAHKIM");
    const scrollCls = isTahkim ? "max-h-[min(200px,36vh)]" : "max-h-[140px]";
    const showHint = dosyalar.length > 4;

    return (
        <div
            className={`mt-1.5 border-t border-white/10 pt-1.5 w-full ${scrollCls} overflow-y-auto overscroll-contain`}
            style={{ scrollbarWidth: "thin" }}
        >
            {showHint ? (
                <div className="sticky top-0 z-[1] mb-1 flex items-center justify-between rounded bg-black/50 px-1.5 py-0.5 text-[9px] text-slate-500 backdrop-blur-sm">
                    <span>{dosyalar.length} dosya</span>
                    <span className="text-slate-600">Kaydır ↓</span>
                </div>
            ) : null}
            <ul className="space-y-0.5 w-full pr-0.5">
                {dosyalar.map((d) => {
                    if (isTahkim || d.on_tahkim_sure_durumu || d.dosya_durumu === "ON_TAHKIM") {
                        const durumEtiket =
                            d.on_tahkim_sure_durumu_etiket ||
                            d.dosya_durumu_etiket ||
                            (d.dosya_durumu === "ON_TAHKIM" ? "Ön tahkimde" : null);
                        return (
                            <li
                                key={d.id}
                                className="rounded border border-white/10 bg-black/25 px-2 py-1"
                            >
                                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0 text-[10px] leading-tight">
                                    <Link
                                        to={`/e/sigorta-dosyalari/${d.id}`}
                                        className="font-mono font-semibold text-[#F3E5AB] hover:underline shrink-0"
                                    >
                                        {d.dosya_no}
                                    </Link>
                                    {d.muvekkil_ad || d.muvekkil_soyad ? (
                                        <span className="font-medium text-slate-200 truncate max-w-[150px]">
                                            {[d.muvekkil_ad, d.muvekkil_soyad].filter(Boolean).join(" ")}
                                        </span>
                                    ) : (
                                        <span className="font-medium text-slate-200 truncate max-w-[150px]">
                                            {d.muvekkil || "—"}
                                        </span>
                                    )}
                                    {d.tc_kimlik ? (
                                        <span className="font-mono text-slate-500 hidden md:inline">
                                            TC {d.tc_kimlik}
                                        </span>
                                    ) : null}
                                    {d.plaka ? (
                                        <span className="font-mono text-slate-500">{d.plaka}</span>
                                    ) : null}
                                    {durumEtiket ? (
                                        <span className="text-[8px] uppercase tracking-wide text-sky-300/90 border border-sky-500/25 rounded px-1 py-px shrink-0">
                                            {durumEtiket}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="mt-0.5 flex flex-wrap gap-x-2.5 gap-y-0 text-[9px] text-slate-500">
                                    {d.basvuru_gonderim_tarihi ? (
                                        <span>Başvuru: {formatTrDate(d.basvuru_gonderim_tarihi)}</span>
                                    ) : null}
                                    {d.on_tahkim_hedef_tarihi ? (
                                        <span>Hedef: {formatTrDate(d.on_tahkim_hedef_tarihi)}</span>
                                    ) : null}
                                    {d.gun_gecen != null ? (
                                        <span className="text-sky-300/80 tabular-nums">{d.gun_gecen} gün</span>
                                    ) : null}
                                    {d.gun_gecikme > 0 ? (
                                        <span className="text-rose-300 tabular-nums">+{d.gun_gecikme} gecikme</span>
                                    ) : d.gun_kaldi != null && d.gun_kaldi >= 0 ? (
                                        <span className="text-amber-300/90 tabular-nums">{d.gun_kaldi} gün kaldı</span>
                                    ) : null}
                                    {d.kalan_sla_gun != null && d.gun_gecikme === 0 ? (
                                        <span className="tabular-nums">SLA {d.kalan_sla_gun} gün</span>
                                    ) : null}
                                </div>
                            </li>
                        );
                    }
                    return (
                        <li key={d.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[10px] py-0.5">
                            <Link
                                to={`/e/sigorta-dosyalari/${d.id}`}
                                className="font-mono font-semibold text-[#F3E5AB] hover:underline shrink-0"
                            >
                                {d.dosya_no}
                            </Link>
                            <span className="text-slate-300">{d.muvekkil || "—"}</span>
                            {d.tc_kimlik ? (
                                <span className="font-mono text-slate-500">TC {d.tc_kimlik}</span>
                            ) : null}
                            {d.plaka ? <span className="font-mono text-slate-600">{d.plaka}</span> : null}
                            {d.kalan_gun != null && d.kalan_gun < 0 ? (
                                <span className="text-rose-300/90">{Math.abs(d.kalan_gun)} gün geçti</span>
                            ) : d.kalan_gun != null ? (
                                <span className="text-amber-300/90">{d.kalan_gun} gün kaldı</span>
                            ) : null}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default function SigortaDosyaTakipPanel({
    period,
    onPeriodChange,
    onRefresh,
    listCount,
    activeFilter,
    onFilterChange,
}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(() => {
        try {
            return localStorage.getItem(LS_EXPANDED) === "1";
        } catch {
            return false;
        }
    });
    const [sideTab, setSideTab] = useState("son");
    const [chartsReady, setChartsReady] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        const params = { yil: period.yil };
        if (period.ay) params.ay = period.ay;
        sigortaDosyaListeOzet(params)
            .then(setData)
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [period.yil, period.ay]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        try {
            localStorage.setItem(LS_EXPANDED, expanded ? "1" : "0");
        } catch {
            /* ignore */
        }
    }, [expanded]);

    useEffect(() => {
        if (!expanded) {
            setChartsReady(false);
            return undefined;
        }
        const frame = window.requestAnimationFrame(() => setChartsReady(true));
        return () => window.cancelAnimationFrame(frame);
    }, [expanded]);

    const chartData = useMemo(() => data?.aylik || [], [data]);
    const uyarilar = data?.uyarilar || [];

    const durumPie = useMemo(
        () =>
            [
                { name: "Yeni", value: data?.yeni_count || 0, color: "#38BDF8" },
                { name: "Başvuru", value: data?.basvuru_count || 0, color: "#D4AF37" },
                { name: "Tahkim", value: data?.tahkim_count || 0, color: "#F59E0B" },
                { name: "Kapalı", value: data?.kapatildi_count || 0, color: "#10B981" },
            ].filter((d) => d.value > 0),
        [data],
    );

    const toggleFilter = (key, value) => {
        if (!onFilterChange) return;
        if (activeFilter?.key === key && activeFilter?.value === value) {
            onFilterChange(null);
        } else {
            onFilterChange({ key, value });
        }
    };

    const activeFilterLabel = useMemo(() => {
        if (!activeFilter) return null;
        const map = {
            YENI: "Yeni",
            BASVURU: "Başvuru",
            TAHKIM: "Tahkim",
            KAPATILDI: "Kapatılan",
            EVRAK_EKSIK: "Evrak eksik",
            YAKLASIYOR: "Tahkime yaklaşan",
            HAZIR: "Tahkime hazır",
            GECIKTI: "Tahkim gecikmiş",
            BEKLIYOR: "Tahkim bekliyor",
        };
        return map[activeFilter.value] || activeFilter.value;
    }, [activeFilter]);

    const barChartRows = period.ay ? chartData.filter((d) => d.ay === period.ay) : chartData;

    return (
        <section className="rounded-lg border border-[#D4AF37]/15 bg-[#121A2F]/80 mb-2 overflow-hidden">
            {/* —— Üst toolbar: tek satır, minimum yükseklik —— */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 border-b border-white/5 bg-[#0f1424]/60">
                <div className="flex items-center gap-2 min-w-0">
                    <FolderOpen className="h-4 w-4 text-[#D4AF37] shrink-0" />
                    <span className="text-sm font-medium text-white whitespace-nowrap">
                        {data?.ay_label || "Özet"} · {period.yil}
                    </span>
                    {listCount != null && (
                        <span className="text-[10px] font-mono text-slate-500 border border-white/10 rounded px-1.5 py-0.5">
                            {listCount} kayıt
                        </span>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-1 flex-1 min-w-[200px]">
                    {AYLAR.map((a) => {
                        const active = period.ay === a.value;
                        return (
                            <button
                                key={a.value}
                                type="button"
                                onClick={() => onPeriodChange({ ...period, ay: a.value })}
                                className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition ${
                                    active
                                        ? "bg-[#D4AF37]/85 text-black"
                                        : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
                                }`}
                            >
                                {a.label}
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                    <select
                        value={period.yil}
                        onChange={(e) =>
                            onPeriodChange({ ...period, yil: Number(e.target.value) })
                        }
                        className="h-7 bg-[#0B0F19] border border-white/10 rounded-md px-2 text-[11px] text-slate-200 focus:outline-none"
                    >
                        {YILLAR.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={() => {
                            load();
                            onRefresh?.();
                        }}
                        disabled={loading}
                        className="inline-flex items-center h-7 px-2 rounded-md border border-white/10 text-slate-400 hover:text-white disabled:opacity-50"
                        title="Yenile"
                    >
                        {loading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className={`inline-flex items-center gap-1 h-7 px-2 rounded-md border text-[11px] transition ${
                            expanded
                                ? "border-[#D4AF37]/40 bg-[#D4AF37]/15 text-[#F3E5AB]"
                                : "border-white/10 text-slate-400 hover:text-slate-200"
                        }`}
                    >
                        <BarChart3 className="h-3.5 w-3.5" />
                        Grafikler
                        {expanded ? (
                            <ChevronUp className="h-3 w-3" />
                        ) : (
                            <ChevronDown className="h-3 w-3" />
                        )}
                    </button>
                </div>
            </div>

            {loading && !data ? (
                <div className="flex items-center gap-2 text-slate-500 text-xs px-3 py-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Özet yükleniyor…
                </div>
            ) : (
                <>
                    {/* Uyarı — tek satır şerit */}
                    {uyarilar.length > 0 && (
                        <div className="px-3 py-1.5 border-b border-white/5 space-y-1.5">
                            {uyarilar.map((u, i) => {
                                const Icon = UYARI_IKON[u.ikon] || AlertTriangle;
                                return (
                                    <div
                                        key={i}
                                        className={`rounded-md border px-2.5 py-1.5 text-[11px] ${UYARI_STIL[u.seviye] || UYARI_STIL.info}`}
                                    >
                                        <div className="flex items-start gap-2">
                                            <Icon className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium">{u.baslik}</div>
                                                <p className="text-slate-400/90 mt-0.5 leading-snug">
                                                    {u.aciklama}
                                                </p>
                                                <UyariDosyaListesi
                                                    dosyalar={u.dosyalar}
                                                    variant={
                                                        u.tip === "on_tahkim_aktif"
                                                            ? "on_tahkim_aktif"
                                                            : String(u.tip || "").startsWith("tahkim")
                                                              ? "tahkim"
                                                              : undefined
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* KPI şeridi — yatay, kompakt */}
                    <div className="px-3 py-2 border-b border-white/5">
                        <div className="flex flex-wrap gap-1.5">
                            <KpiChip
                                label="Toplam"
                                value={data?.toplam_dosya}
                                accent="violet"
                                title={`Toplam dosya · ${data?.donem_kayit || 0} dönem kaydı`}
                            />
                            <KpiChip
                                label="Yeni"
                                value={data?.yeni_count}
                                accent="sky"
                                active={
                                    activeFilter?.key === "dosya_durumu" &&
                                    activeFilter?.value === "YENI"
                                }
                                onClick={() => toggleFilter("dosya_durumu", "YENI")}
                            />
                            <KpiChip
                                label="Başvuru"
                                value={data?.basvuru_count}
                                accent="gold"
                                active={
                                    activeFilter?.key === "dosya_durumu" &&
                                    activeFilter?.value === "BASVURU"
                                }
                                onClick={() => toggleFilter("dosya_durumu", "BASVURU")}
                            />
                            <KpiChip
                                label="Tahkim"
                                value={data?.tahkim_count}
                                accent="amber"
                                active={
                                    activeFilter?.key === "dosya_durumu" &&
                                    activeFilter?.value === "TAHKIM"
                                }
                                onClick={() => toggleFilter("dosya_durumu", "TAHKIM")}
                            />
                            <KpiChip
                                label="Tahkim bekleyen"
                                value={data?.tahkim_bekleyen_count}
                                accent="sky"
                                title="Başvuru süresi devam eden — henüz tahkime geçilmedi"
                                active={
                                    activeFilter?.key === "on_tahkim_sure_durumu" &&
                                    activeFilter?.value === "BEKLIYOR"
                                }
                                onClick={() => toggleFilter("on_tahkim_sure_durumu", "BEKLIYOR")}
                            />
                            <KpiChip
                                label="Tahkime yaklaşan"
                                value={data?.tahkim_yaklasan_count}
                                accent="amber"
                                title="Başvuru bekleme süresinin bitimine ≤3 gün"
                                active={
                                    activeFilter?.key === "on_tahkim_sure_durumu" &&
                                    activeFilter?.value === "YAKLASIYOR"
                                }
                                onClick={() => toggleFilter("on_tahkim_sure_durumu", "YAKLASIYOR")}
                            />
                            <KpiChip
                                label="Tahkime hazır"
                                value={data?.tahkim_hazir_count}
                                accent="gold"
                                title="Hedef tarih bugün — ön tahkime geçilebilir"
                                active={
                                    activeFilter?.key === "on_tahkim_sure_durumu" &&
                                    activeFilter?.value === "HAZIR"
                                }
                                onClick={() => toggleFilter("on_tahkim_sure_durumu", "HAZIR")}
                            />
                            <KpiChip
                                label="Tahkim gecikmiş"
                                value={data?.tahkim_gecikti_count}
                                accent="rose"
                                title="Başvuru bekleme süresi geçti"
                                active={
                                    activeFilter?.key === "on_tahkim_sure_durumu" &&
                                    activeFilter?.value === "GECIKTI"
                                }
                                onClick={() => toggleFilter("on_tahkim_sure_durumu", "GECIKTI")}
                            />
                            <KpiChip
                                label="Evrak eksik"
                                value={data?.evrak_eksik_count}
                                accent="rose"
                                active={
                                    activeFilter?.key === "dosya_durumu" &&
                                    activeFilter?.value === "EVRAK_EKSIK"
                                }
                                onClick={() => toggleFilter("dosya_durumu", "EVRAK_EKSIK")}
                            />
                            <KpiChip
                                label="Kapatılan"
                                value={data?.kapatildi_count}
                                accent="emerald"
                                active={
                                    activeFilter?.key === "dosya_durumu" &&
                                    activeFilter?.value === "KAPATILDI"
                                }
                                onClick={() => toggleFilter("dosya_durumu", "KAPATILDI")}
                            />
                        </div>
                        {activeFilterLabel && (
                            <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[#D4AF37]">
                                <span>Filtre: {activeFilterLabel}</span>
                                <button
                                    type="button"
                                    onClick={() => onFilterChange?.(null)}
                                    className="inline-flex items-center gap-0.5 text-slate-500 hover:text-white"
                                >
                                    <X className="h-3 w-3" />
                                    Temizle
                                </button>
                            </div>
                        )}
                    </div>

                    <TahkimSureDetayPanel
                        detay={data?.tahkim_detay}
                        onFilterChange={onFilterChange}
                        defaultOpen={false}
                    />

                    {/* Grafikler — varsayılan kapalı, açılınca kompakt tek satır */}
                    {expanded && (
                        <div className="px-3 py-2 border-b border-white/5 bg-[#0B0F19]/30">
                            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_200px] gap-3 items-stretch">
                                <div className="min-w-0">
                                    <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                                        <TrendingUp className="h-3 w-3 text-[#D4AF37]" />
                                        {period.ay
                                            ? `${data?.ay_label} kayıtları`
                                            : `${period.yil} aylık yeni / kapatılan`}
                                    </div>
                                    <ChartBox height={110} ready={chartsReady} debounce={48}>
                                        <BarChart data={barChartRows} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                            <XAxis
                                                dataKey="ay_label"
                                                tick={{ fill: "#64748b", fontSize: 9 }}
                                                axisLine={false}
                                                tickLine={false}
                                            />
                                            <YAxis
                                                tick={{ fill: "#64748b", fontSize: 9 }}
                                                allowDecimals={false}
                                                axisLine={false}
                                                tickLine={false}
                                                width={28}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    background: "#0B0F19",
                                                    border: "1px solid #334155",
                                                    borderRadius: 6,
                                                    fontSize: 11,
                                                }}
                                            />
                                            <Bar dataKey="yeni_dosya" name="Yeni" fill="#8B5CF6" radius={[2, 2, 0, 0]} maxBarSize={24} />
                                            <Bar dataKey="kapatilan_dosya" name="Kapatılan" fill="#10B981" radius={[2, 2, 0, 0]} maxBarSize={24} />
                                        </BarChart>
                                    </ChartBox>
                                </div>

                                {durumPie.length > 0 && (
                                    <div className="flex flex-col items-center justify-center lg:border-x lg:border-white/5 lg:px-3">
                                        <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">
                                            Durum
                                        </div>
                                        <ChartBox height={90} width={120} ready={chartsReady} debounce={48}>
                                            <PieChart>
                                                <Pie
                                                    data={durumPie}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={22}
                                                    outerRadius={38}
                                                    paddingAngle={2}
                                                >
                                                    {durumPie.map((entry) => (
                                                        <Cell key={entry.name} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    contentStyle={{
                                                        background: "#0B0F19",
                                                        border: "1px solid #334155",
                                                        borderRadius: 6,
                                                        fontSize: 10,
                                                    }}
                                                />
                                            </PieChart>
                                        </ChartBox>
                                        <div className="flex flex-wrap justify-center gap-x-2 gap-y-0.5">
                                            {durumPie.map((d) => (
                                                <span
                                                    key={d.name}
                                                    className="text-[9px] text-slate-500 inline-flex items-center gap-0.5"
                                                >
                                                    <span
                                                        className="h-1.5 w-1.5 rounded-full"
                                                        style={{ background: d.color }}
                                                    />
                                                    {d.name} {d.value}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="min-w-0 flex flex-col">
                                    <div className="flex gap-0.5 mb-1">
                                        {[
                                            ["son", "Son dosyalar"],
                                            ["marka", "Markalar"],
                                            ["konu", "Konular"],
                                        ].map(([id, lbl]) => (
                                            <button
                                                key={id}
                                                type="button"
                                                onClick={() => setSideTab(id)}
                                                className={`flex-1 text-[9px] py-0.5 rounded ${
                                                    sideTab === id
                                                        ? "bg-white/10 text-slate-200"
                                                        : "text-slate-600 hover:text-slate-400"
                                                }`}
                                            >
                                                {lbl}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex-1 overflow-y-auto max-h-[100px] text-[10px]">
                                        {sideTab === "son" &&
                                            (data?.son_dosyalar || []).slice(0, 4).map((d) => (
                                                <Link
                                                    key={d.id}
                                                    to={`/e/sigorta-dosyalari/${d.id}`}
                                                    className="flex justify-between gap-1 py-0.5 border-b border-white/5 last:border-0 hover:text-[#F3E5AB]"
                                                >
                                                    <span className="font-mono text-slate-300 truncate">
                                                        {d.dosya_no}
                                                    </span>
                                                    <span className="text-slate-600 shrink-0">{d.plaka}</span>
                                                </Link>
                                            ))}
                                        {sideTab === "marka" &&
                                            (data?.en_cok_marka || []).slice(0, 5).map((m, idx) => (
                                                <div key={idx} className="flex justify-between py-0.5 capitalize">
                                                    <span className="text-slate-400 truncate">{m.marka.toLowerCase()}</span>
                                                    <span className="text-[#F3E5AB] font-mono">{m.count}</span>
                                                </div>
                                            ))}
                                        {sideTab === "konu" && (
                                            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-slate-500">
                                                <span>
                                                    Değer kaybı{" "}
                                                    <b className="text-white">{data?.deger_kaybi_count || 0}</b>
                                                </span>
                                                <span>
                                                    Pert farkı{" "}
                                                    <b className="text-white">{data?.pert_farki_count || 0}</b>
                                                </span>
                                                <span>
                                                    Yaralanmalı{" "}
                                                    <b className="text-white">{data?.yaralanmali_count || 0}</b>
                                                </span>
                                                <span>
                                                    Süre geçmiş{" "}
                                                    <b className="text-rose-300">{data?.sure_gecmis || 0}</b>
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </section>
    );
}
